import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AssignmentOutlined,
  AttachMoney as PriceIcon,
  Inventory as DepartmentIcon,
  Inventory2 as ConsumablesIcon,
  Medication as DrugRegisterIcon,
  ReceiptLongOutlined as PurchaseIcon,
} from '@mui/icons-material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ExploreOffIcon from '@mui/icons-material/ExploreOff';

import DepartmentLayout from '../Layout/DepartmentLayout';
import Home from './Home';
import PurchaseOrders from './PurchaseOrders';
import ConsumablePurchaseOrders from './ConsumablePurchaseOrders';
import ManageRequisitions from './ManageRequisitions';
import ProductPricing from './ProductPricing';
import StockAnalysis from './StockAnalysis';
import Consumables from './Consumables';
import ConsumablePricing from './ConsumablePricing';
import ConsumableStockAnalysis from './ConsumableStockAnalysis';
import { getRequisitions } from '../BaseURL';

// How often the sidebar polls for new pending requisitions (ms).
const PENDING_POLL_INTERVAL = 60_000;

export default function Procurement() {
  // Number of *requester groups* with pending items (e.g. 3 means three
  // distinct people have submitted requisitions awaiting approval — even if
  // those three together total 200 individual lines).
  const [pendingGroups, setPendingGroups] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const fetchCount = async () => {
      try {
        const res = await axios.get(getRequisitions, { params: { status: 'pending' } });
        if (cancelled) return;
        const rows = Array.isArray(res?.data?.results) ? res.data.results : [];
        const distinctRequesters = new Set(
          rows
            .filter(r => r.status === 'pending')
            .map(r => (r.requestedBy || 'Unknown').trim())
        );
        setPendingGroups(distinctRequesters.size);
      } catch {
        // Network errors are silent — just leave the previous count alone.
      }
    };

    fetchCount();
    timer = setInterval(fetchCount, PENDING_POLL_INTERVAL);

    // Re-poll when the tab regains focus so a returning user sees fresh counts.
    const onVisible = () => { if (!document.hidden) fetchCount(); };
    document.addEventListener('visibilitychange', onVisible);

    // Re-poll the moment any approve/reject is committed inside Manage
    // Requisitions — the page dispatches this event after a successful save
    // so the sidebar bell updates instantly.
    const onChanged = () => fetchCount();
    window.addEventListener('totalcare:requisitions-changed', onChanged);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('totalcare:requisitions-changed', onChanged);
    };
  }, []);

  // Menu is grouped by `section` so the sidebar shows two clear families —
  // everything to do with drugs lives under "Drugs", everything to do with
  // non-drug supplies lives under "Consumables". A small "General" block
  // at the bottom holds anything that's shared (e.g. requisitions).
  //
  // NOTE — `key` values for the original four items are kept verbatim
  // ('Home', 'Purchase Orders', 'ProductPricing', 'StockAnalysis',
  // 'Manage Requisitions') because the role-permission system in the DB
  // is indexed by component_name. Renaming the visible label is safe;
  // renaming the key would lock users out who previously had access.
  const menuItems = useMemo(() => [
    // ── Drugs ──────────────────────────────────────────────────────────
    { section: 'Drugs', key: 'Home',            label: 'Drug Registration',   icon: <DrugRegisterIcon />, component: Home },
    { section: 'Drugs', key: 'Purchase Orders', label: 'Drug Purchases',      icon: <PurchaseIcon />,     component: PurchaseOrders },
    { section: 'Drugs', key: 'ProductPricing',  label: 'Drug Pricing',        icon: <PriceIcon />,        component: ProductPricing },
    { section: 'Drugs', key: 'StockAnalysis',   label: 'Drug Stock Analysis', icon: <ExploreOffIcon />,   component: StockAnalysis },

    // ── Consumables ────────────────────────────────────────────────────
    { section: 'Consumables', key: 'Consumables',                   label: 'Consumables Register',   icon: <ConsumablesIcon />, component: Consumables },
    { section: 'Consumables', key: 'Purchase Orders - Consumables', label: 'Consumable Purchases',   icon: <PurchaseIcon />,    component: ConsumablePurchaseOrders },
    { section: 'Consumables', key: 'ConsumablePricing',             label: 'Consumable Pricing',     icon: <PriceIcon />,       component: ConsumablePricing },
    { section: 'Consumables', key: 'ConsumableStockAnalysis',       label: 'Consumable Stock Analysis', icon: <ExploreOffIcon />, component: ConsumableStockAnalysis },

    // ── General (shared across both) ───────────────────────────────────
    {
      section: 'General',
      key: 'Manage Requisitions',
      label: 'Manage Requisitions',
      icon: <AddShoppingCartIcon />,
      component: ManageRequisitions,
      badge: pendingGroups,
    },
  ], [pendingGroups]);

  return (
    <DepartmentLayout
      departmentId={28}
      departmentName="Procurement & Supplies"
      departmentIcon={<DepartmentIcon />}
      menuItems={menuItems}
    />
  );
}
